import { ErrorBoundary } from '@datadog/browser-rum-react'
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6'
import { LinearProgress } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import Maintenance from '../components/Maintenance'
import Pendo from '../components/Pendo'
import AdminProvider from '../providers/AdminProvider'
import ApolloProvider from '../providers/apollo/Provider'
import FirebaseBlockingProvider from '../providers/FirebaseProvider'
import RumProvider from '../providers/rum/RumProvider'
import Settings from './buckets/components/settings'
import ConfirmEmail from './buckets/ConfirmEmail'
import { ErrorDataDogFallback } from '../providers/ErrorBoundary'

// const AccountRouter = lazy(() => import('./account/Router'))
const SubscriptionRouter = lazy(() => import('./subscription/Router'))
const IntegrationsRouter = lazy(() => import('./integrations/Router'))
const AuthRouter = lazy(() => import('./auth/Router'))
const ConversionRouter = lazy(() => import('./conversion/Router'))
const BucketsRouter = lazy(() => import('./buckets/Router'))
const ManageBuckets = lazy(() => import('./buckets/ManageBuckets'))
const AcceptInvite = lazy(() => import('./buckets/AcceptInvite'))
const AcceptTransfer = lazy(() => import('./buckets/AcceptBucketOwnerInvite'))
const SharedRouter = lazy(() => import('./shared/Router'))
const LegacySharedRouter = lazy(() => import('./shared/LegacyRouter'))
const Create = lazy(() => import('./buckets/Create'))

function RootLayout() {
  const maintenanceIsEnabled = useFlag('maintenanceIsEnabled')

  return (
    <RumProvider>
      <FirebaseBlockingProvider>
        <ApolloProvider>
          <AdminProvider>
            <Pendo />
            {maintenanceIsEnabled && <Maintenance />}
            <ErrorBoundary fallback={ErrorDataDogFallback}>
              <Suspense fallback={<LinearProgress />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </AdminProvider>
        </ApolloProvider>
      </FirebaseBlockingProvider>
    </RumProvider>
  )
}

function ResetPassword() {
  const location = useLocation()

  return <Navigate to={`/auth/reset-password${location.search}`} replace />
}

function ConfirmationRedirect() {
  const location = useLocation()

  return <Navigate to={`/auth/confirmation${location.search}`} replace />
}

function ConditionalIntegrationsRoute() {
  const useWhatsAppIntegration = useFlag('useWhatsAppIntegration')

  if (!useWhatsAppIntegration) {
    return <Navigate to="/" replace />
  }

  return <IntegrationsRouter />
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Redirects from legacy site
      { path: 'explore', element: <Navigate to="/" replace /> },
      { path: 'login', element: <Navigate to="/auth/login" replace /> },
      { path: 'resetPassword', element: <ResetPassword /> },
      { path: 'confirmation', element: <ConfirmationRedirect /> },

      // Primary routes
      { path: 'transfer/:transferId', element: <AcceptTransfer /> },
      { path: 'invite/:invitationId', element: <AcceptInvite /> },
      { path: 'share/:shareId/*', element: <SharedRouter /> },
      { path: 'buckets', element: <ManageBuckets /> },
      { path: 'settings', element: <Settings /> },
      { path: 'subscription/*', element: <SubscriptionRouter /> },
      { path: 'view-by-admin/*', element: <div /> },
      { path: 'confirm/:id', element: <ConfirmEmail /> },
      { path: 'confirm/', element: <ConfirmEmail /> },
      { path: 'bucket/create', element: <Create /> },
      { path: 'bucket/:bucketId/*', element: <BucketsRouter /> },
      { path: 'auth/*', element: <AuthRouter /> },
      { path: 'u/:userId', element: <Navigate to="/buckets" replace /> },
      { path: 'u/:userId/*', element: <LegacySharedRouter /> },
      { path: 'whatsapp', element: <Navigate to="/integrations/whatsapp/setup" replace /> },
      { path: '/integrations/*', element: <ConditionalIntegrationsRoute /> },
      { path: '*', element: <ConversionRouter /> }
    ]
  }
])

export default router
